import LogoutIcon from '@mui/icons-material/Logout'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import MuiDrawer from '@mui/material/Drawer'
import Fade from '@mui/material/Fade'
import useMediaQuery from '@mui/material/useMediaQuery'
import { TFunction } from 'i18next'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import BaseButton from 'src/components/Buttons/BaseButton'
import {
  accountRows,
  switchRow
} from 'src/components/Layouts/DashboardLayout/accountNav'
import NavSkeleton from 'src/components/Layouts/DashboardLayout/NavSkeleton'
import useAccountNav from 'src/components/Layouts/DashboardLayout/useAccountNav'
import { useAuthProvider } from 'src/context/AuthProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useNotificationCounts, {
  INotificationCounts
} from 'src/hooks/useNotificationCounts'
import useWhatsappCityHint from 'src/hooks/useWhatsappCityHint'
import { RedirectUrls } from 'src/types/types'
import { deferToIdle } from 'src/utils/deferToIdle'
import { hasAnyOnDemandCoverage } from 'src/utils/onDemand'
import ga from 'src/utils/pixels/gtag'
import { getSubmitLocation } from 'src/utils/submitLocation'
import { appendCityHint, buildWhatsappUrl } from 'src/utils/whatsapp'
import {
  DRAWER_BUSINESS_LINKS,
  DRAWER_CITY_LINKS,
  DRAWER_PERSONAL_LINKS,
  DrawerLink
} from './constants'
import LanguageToggle from './LanguageToggle'

/** Rows shown while dashboardContext resolves — the tab bar is 4 items. */
const ACCOUNT_SKELETON_ROWS = 4

/**
 * Below this width the drawer is the whole screen and FADES; from here up
 * it is a 420px side panel and SLIDES in from the right (MUI Drawer's own
 * default for `anchor="right"`). Mirrors the `sl` breakpoint used by the
 * paper classes below — keep the two in sync.
 */
const SIDE_PANEL_MIN_WIDTH_PX = 650

// --- Shared sub-components (no closure dependencies) ---

/** Same eyebrow vocabulary as the desktop mega-menu column headings. */
const SectionLabel = ({ label }: { label: string }) => (
  <div className="px-5 pt-4 pb-1.5 font-display text-[11px] tracking-[0.22em] text-blue-800 uppercase">
    {label}
  </div>
)

const Separator = () => <div className="mx-5 my-2 border-t border-ink-100/15" />

const NotificationBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null
  const display = count > 9 ? '9+' : count
  return (
    <span className="flex min-w-[18px] items-center justify-center rounded-full bg-red-500/90 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-white-500">
      {display}
    </span>
  )
}

/**
 * Resolves a drawer link's `gateBy` flag against runtime coverage state.
 * Returns `true` when the link should render, `false` to omit it.
 */
const isDrawerLinkVisible = (link: DrawerLink): boolean => {
  if (link.gateBy === 'on-demand-coverage') return hasAnyOnDemandCoverage()
  return true
}

/**
 * Context every drawer link needs. Bundled so the ~25 call sites below can
 * spread one object instead of threading five props each.
 */
type DrawerLinkContext = {
  t: TFunction<'nav'>
  counts: INotificationCounts
  pathname: string
  isHost: boolean
  onNavigate: () => void
}

/**
 * Must stay at module scope. Declaring this inside `Drawer` makes it a new
 * component *type* on every render, so React tears down and remounts all
 * ~25 links (losing their DOM and state) on any drawer state change.
 */
const DrawerNavLink = ({
  link,
  t,
  counts,
  pathname,
  isHost,
  onNavigate
}: { link: DrawerLink } & DrawerLinkContext) => {
  const badgeCount = link.badge ? link.badge(counts) : 0
  // Home link must match exactly; every other path startsWith('/').
  const isActive =
    link.url === '/' ? pathname === '/' : pathname.startsWith(link.url)

  return (
    <Link
      prefetch={false}
      href={link.url}
      onClick={() => {
        ga.event('nav_link_click', {
          url: link.url,
          surface: 'mobile_drawer'
        })
        onNavigate()
      }}
      aria-current={isActive ? 'page' : undefined}
      className={`flex items-center justify-between px-5 py-2.5 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-800 ${
        isActive
          ? isHost
            ? 'bg-cream-300 font-semibold text-ink-700'
            : 'bg-cream-300 text-blue-800'
          : 'text-ink-700 hover:bg-cream-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <i className={`si ${link.icon} text-xl text-ink-200`} />
        <span className="text-base font-medium">
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            t(link.translationKey as any)
          }
        </span>
      </div>
      <NotificationBadge count={badgeCount} />
    </Link>
  )
}

// --- Main component ---

type IProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const Drawer = ({ isOpen, setIsOpen }: IProps) => {
  const { t } = useTranslation('nav')
  const { t: tCommon } = useTranslation('common')
  // `lg`, not `md`: the hamburger shows below 1024 (the mega menus only
  // render from lg), so the drawer must exist there too.
  const { isDownLg } = useBreakpoints()
  const isSidePanel = useMediaQuery(`(min-width:${SIDE_PANEL_MIN_WIDTH_PX}px)`)
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const { isAuthenticated, currentUser, logout } = useAuthProvider()
  const counts = useNotificationCounts()
  const router = useRouter()
  const cityHintLine = useWhatsappCityHint()

  const accountNav = useAccountNav(counts)
  // Role follows the dashboard's own rule (dashboardContext), not the declared
  // `interest` alone — a renter company team is not a host.
  const isHost = accountNav.role === 'host'
  const close = () => setIsOpen(false)

  // Fire once each time the drawer actually becomes visible. Gated on
  // `isDownLg` because the parent leaves `isOpen=true` set on desktop
  // (where the drawer no-ops), and we only want a fire when the user can
  // actually see it. `path` lets us tell which page drove the open.
  //
  // Deferred off the interaction. React flushes passive effects
  // synchronously for discrete events, so firing here directly puts the
  // whole pixel fan-out inside the tap: `gtag('event', …)` alone measured
  // 30-56 ms on a 4x-throttled mobile main thread, and the drawer cannot
  // paint until it returns.
  useEffect(() => {
    if (!isOpen || !isDownLg) return
    return deferToIdle(() =>
      ga.event('nav_drawer_open', { path: router.pathname })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDownLg])

  if (!isDownLg) return null

  const firstName = currentUser?.firstname || ''
  const initial = firstName.charAt(0).toUpperCase()
  const profilePicUrl = currentUser?.profilePicture?.url

  const linkContext: DrawerLinkContext = {
    t,
    counts,
    pathname: router.pathname,
    isHost,
    onNavigate: close
  }

  const whatsappUrl = buildWhatsappUrl(
    appendCityHint(t('NAV_DRAWER.WHATSAPP_MSG'), cityHintLine)
  )

  // --- Header ---

  const renderHeader = () => {
    if (!isAuthenticated || !currentUser) {
      // Unauthenticated header — one auth link (the login page handles both
      // sign-in and sign-up; the old "Iniciar sesión · Crear cuenta" pair
      // pointed at the same URL twice).
      return (
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between">
            <Image
              src="/logos/logo.png"
              alt="SpotMe"
              width={130}
              height={32}
              className="object-contain"
              style={{ height: '32px', width: 'auto' }}
            />
            <button
              type="button"
              onClick={close}
              className="flex size-10 items-center justify-center rounded-full transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
              aria-label={tCommon('A11Y.CLOSE_MENU')}
            >
              <i className="si si-close text-xl text-ink-700" aria-hidden />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <LanguageToggle />
            <Link
              prefetch={false}
              href={RedirectUrls.Login}
              onClick={close}
              className="text-sm font-medium text-blue-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
            >
              {t('NAV_DRAWER.BUTTON_LOGIN')}
            </Link>
          </div>
        </div>
      )
    }

    // Authenticated header
    const headerBg = isHost
      ? 'bg-gradient-to-br from-black-700 via-black-600 to-blue-700'
      : 'bg-blue-200'
    const nameClass = isHost ? 'text-white-500' : 'text-ink-700'
    // Renter header sits on blue-200: ink-400 is 4.5:1 there, ink-200 is not.
    const emailClass = isHost ? 'text-grey-300' : 'text-ink-400'
    const badgeBg = isHost
      ? 'bg-white-500 text-ink-700'
      : 'bg-blue-500/15 text-ink-700'
    const avatarBg = isHost
      ? 'bg-white-500 text-ink-700'
      : 'bg-blue-500 text-white-500'
    const closeHover = isHost
      ? 'hover:bg-white-500/10 focus-visible:outline-white-500'
      : 'hover:bg-black-500/10 focus-visible:outline-blue-800'
    const closeIconColor = isHost ? 'text-white-500' : 'text-ink-700'
    const badgeLabel = isHost
      ? t('NAV_DRAWER.BADGE_HOST')
      : t('NAV_DRAWER.BADGE_RENTER')

    return (
      <div className={`relative overflow-hidden px-5 pt-4 pb-4 ${headerBg}`}>
        {/* Soft glow for depth on the host (dark) header. Radial gradient,
            not a blur() filter — this layer moves with the drawer's open
            transition every frame, and filters there are costly on phone
            GPUs. */}
        {isHost && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-28 size-72 rounded-full bg-[radial-gradient(circle,_rgba(0,164,255,0.30)_0%,_transparent_65%)]"
          />
        )}
        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-3">
            {profilePicUrl ? (
              <Image
                src={profilePicUrl}
                alt={firstName}
                width={48}
                height={48}
                className="size-12 rounded-full object-cover"
              />
            ) : (
              <div
                className={`flex size-12 items-center justify-center rounded-full text-lg font-semibold ${avatarBg}`}
              >
                {initial}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className={`text-lg font-semibold ${nameClass}`}>
                {currentUser.firstname} {currentUser.lastname}
              </div>
              <div className={`truncate text-sm ${emailClass}`}>
                {currentUser.email}
              </div>
              <span
                className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeBg}`}
              >
                {badgeLabel}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className={`flex size-10 items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${closeHover}`}
            aria-label={tCommon('A11Y.CLOSE_MENU')}
          >
            <i
              className={`si si-close text-xl ${closeIconColor}`}
              aria-hidden
            />
          </button>
        </div>
        <div className="relative mt-2">
          <LanguageToggle darkSurface={isHost} />
        </div>
      </div>
    )
  }

  // --- Body ---

  const renderBody = () => {
    const panelHrefs = new Set(accountNav.items.map((item) => item.href))
    const accountLinks: DrawerLink[] = [
      ...accountNav.items.map((item) => ({
        translationKey: item.labelKey,
        url: item.href,
        icon: item.icon,
        badge: () => item.badge
      })),
      // Inicio, Admin and Ayuda already have their own rows in this drawer.
      ...accountRows({
        role: accountNav.role,
        isAdmin: !!currentUser?.isAdmin,
        withHome: false,
        withAdmin: false,
        withHelp: false
      })
        // An admin's Cuentas already opens the teams list.
        .filter((row) => !panelHrefs.has(row.href))
        .map((row) => ({
          translationKey: row.labelKey,
          url: row.href,
          icon: row.icon
        }))
    ]
    // Renters who host nothing get the footer's "publica tu espacio" line
    // instead — a second publish row here would say the same thing twice.
    const modeSwitch =
      !accountNav.loading && (isHost || accountNav.hostsSomething)
        ? switchRow({
            role: accountNav.role,
            hostsSomething: accountNav.hostsSomething,
            publishHref: getSubmitLocation(currentUser)
          })
        : null

    return (
      <nav className="flex-1 overflow-y-auto" role="navigation">
        {/* Home link */}
        <DrawerNavLink
          link={{
            translationKey: 'NAV_DRAWER.LINK_HOME',
            url: '/',
            icon: 'si-house'
          }}
          {...linkContext}
        />

        {/* Admin link — prominent, right after Home */}
        {currentUser?.isAdmin && (
          <Link
            prefetch={false}
            href={RedirectUrls.Admin}
            onClick={close}
            className="mx-5 mt-2 flex items-center justify-between rounded-lg bg-blue-100 px-4 py-3 transition-colors hover:bg-blue-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          >
            <div className="flex items-center gap-3">
              <i className="si si-house-shield text-xl text-blue-500" />
              <span className="text-base font-semibold text-blue-800">
                {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  t('NAV_DRAWER.LINK_ADMIN' as any)
                }
              </span>
            </div>
            <NotificationBadge count={counts.admin} />
          </Link>
        )}

        <Separator />

        {/* Account section (authenticated only) */}
        {isAuthenticated && currentUser && (
          <>
            <SectionLabel label={t('NAV_DRAWER.SECTION_ACCOUNT')} />
            {accountNav.loading ? (
              <div className="px-2">
                <NavSkeleton rows={ACCOUNT_SKELETON_ROWS} />
              </div>
            ) : (
              accountLinks.map((link) => (
                <DrawerNavLink key={link.url} link={link} {...linkContext} />
              ))
            )}
            {isHost && (
              <>
                <Separator />
                <SectionLabel label={t('NAV_DRAWER.SECTION_QUICK_ACTIONS')} />
                {/* Straight to the wizard: a signed-in host has no use for
                    the /anfitrion pitch. Referidos lives in the account rows. */}
                <DrawerNavLink
                  link={{
                    translationKey: 'NAV_DRAWER.LINK_NEW_SPOT',
                    url: getSubmitLocation(currentUser),
                    icon: 'si-house-plus'
                  }}
                  {...linkContext}
                />
              </>
            )}
            <Separator />

            {/* Mode switch — this drawer only renders on public pages now,
                so the old "only on /dashboard/*" gate never showed it. */}
            {modeSwitch && (
              <>
                <Link
                  prefetch={false}
                  href={modeSwitch.href}
                  onClick={close}
                  className="mx-5 my-2 flex items-center gap-3 rounded-lg bg-cream-200 px-4 py-3 text-ink-700 transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  <i className={`si ${modeSwitch.icon} text-xl`} />
                  <span className="text-base font-medium">
                    {t(modeSwitch.labelKey)}
                  </span>
                </Link>
                <Separator />
              </>
            )}
          </>
        )}

        {/* Browse — split by who rents it. The flat seven-noun list mixed
            a private renter's three options with four B2B ones. */}
        <SectionLabel label={t('NAV_DRAWER.SECTION_PERSONAL')} />
        {DRAWER_PERSONAL_LINKS.filter(isDrawerLinkVisible).map((link) => (
          <DrawerNavLink key={link.url} link={link} {...linkContext} />
        ))}

        <SectionLabel label={t('NAV_DRAWER.SECTION_BUSINESS')} />
        {DRAWER_BUSINESS_LINKS.map((link) => (
          <DrawerNavLink key={link.url} link={link} {...linkContext} />
        ))}

        <Separator />

        {/* Cities — the desktop mega menu had these, the drawer did not, so
            the two devices carried different site maps (2026-09-09). */}
        <SectionLabel label={t('NAV_DRAWER.SECTION_CITIES')} />
        {DRAWER_CITY_LINKS.map((link) => (
          <DrawerNavLink key={link.url} link={link} {...linkContext} />
        ))}

        <Separator />

        {/* Help */}
        <DrawerNavLink
          link={{
            translationKey: 'NAV_DRAWER.LINK_HELP',
            url: RedirectUrls.Help,
            icon: 'si-ask'
          }}
          {...linkContext}
        />

        {/* Logout */}
        {isAuthenticated && (
          <>
            <Separator />
            <button
              type="button"
              onClick={() => {
                logout()
                close()
              }}
              className="flex w-full items-center gap-3 px-5 py-2.5 text-red-500 transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-800"
            >
              <LogoutIcon className="text-xl" />
              <span className="text-base font-medium">
                {t('NAV_DRAWER.BUTTON_LOGOUT')}
              </span>
            </button>
          </>
        )}
      </nav>
    )
  }

  // --- Footer (pinned) ---
  //
  // The pinned action used to be the host CTA alone, on the device where
  // ~90% of traffic is renters. Now: search first, WhatsApp (the channel
  // most of these visitors convert on) second, and the host ask demoted to
  // a text line — hidden for users who already are hosts. Stacked, not a
  // row: three labelled actions do not fit one row at 320px.
  const renderFooter = () => (
    <div className="border-t border-ink-100/15 bg-white-500 px-5 pt-3 pb-4">
      <BaseButton
        href={RedirectUrls.Search}
        variant="contained"
        rounded
        size="large"
        className="w-full"
        onClick={() => {
          ga.event('nav_link_click', {
            url: RedirectUrls.Search,
            surface: 'mobile_drawer'
          })
          close()
        }}
      >
        {t('NAV_DRAWER.BUTTON_SEARCH')}
      </BaseButton>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            ga.event('nav_link_click', {
              url: 'whatsapp',
              surface: 'mobile_drawer'
            })
            close()
          }}
          className="inline-flex items-center gap-2 font-medium text-ink-700 transition-colors hover:text-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
        >
          <WhatsAppIcon className="text-[18px] text-blue-500" />
          <span>{t('NAV_DRAWER.WHATSAPP')}</span>
        </a>
        {!isHost && (
          <Link
            prefetch={false}
            href={RedirectUrls.Anfitrion}
            onClick={() => {
              // Same `anfitrion_click` family as the desktop pill
              // (`nav_desktop`) and the footer card (`footer_banner`).
              ga.event('anfitrion_click', { location: 'nav_mobile' })
              close()
            }}
            className="text-ink-200 transition-colors hover:text-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          >
            {`${t('NAV_DRAWER.HOST_HINT')} `}
            <span className="font-medium text-ink-700 underline decoration-ink-100/40 underline-offset-4">
              {t('NAV_DRAWER.HOST_LINK')}
            </span>
          </Link>
        )}
      </div>
    </div>
  )

  // Transition: full-screen on phones → fade; side panel from `sl` up →
  // MUI Drawer's default Slide (direction follows `anchor`). The old Grow
  // scaled the whole sheet out of the hamburger corner, which read as a
  // zoom rather than a panel. Reduced motion → no animated frames.
  const timeout = reduceMotion
    ? 0
    : isSidePanel
      ? { enter: 220, exit: 160 }
      : { enter: 180, exit: 140 }

  return (
    <MuiDrawer
      anchor="right"
      open={isOpen}
      onClose={close}
      {...(isSidePanel ? {} : { slots: { transition: Fade } })}
      slotProps={{
        paper: {
          className:
            'h-[100dvh] w-screen max-h-none shadow-none sl:w-[420px] sl:max-w-[90vw] sl:rounded-l-2xl sl:shadow-2xl'
        },
        transition: { timeout }
      }}
      sx={{ zIndex: 1300 }}
    >
      <div className="flex h-full flex-col bg-white-500 pb-safe">
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </div>
    </MuiDrawer>
  )
}

export default React.memo(Drawer)
